'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Pre, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml'

const TrainingLoopViz = dynamic(() => import('../_components/widgets/MLWidgets').then(m => m.TrainingLoopViz), { ssr: false })

export default function TrainingLoop() {
  return (
    <Lesson>
      <Step>
        <H>Let the slope do the work</H>
        <P>
          The loss <M>{'\\pink{L}(\\green{w}, \\green{b})'}</M> is a landscape over the two parameters. We know how to go
          downhill on a landscape: compute the gradient, step against it. All we need are the two partial derivatives. Using the
          chain rule on each squared error <M>{'(\\green{w}x_i + \\green{b} - y_i)^2'}</M>:
        </P>
        <MB>{'\\pink{\\frac{\\partial L}{\\partial w}} = \\frac{2}{n}\\sum_{i} \\underbrace{(\\amber{\\hat{y}_i} - y_i)}_{\\text{error}}\\, \\blue{x_i} \\qquad \\pink{\\frac{\\partial L}{\\partial b}} = \\frac{2}{n}\\sum_{i} (\\amber{\\hat{y}_i} - y_i)'}</MB>
        <Callout kind="plain">
          The outer square contributes <M>{'2 \\times \\text{error}'}</M>; the inner part <M>{'w x_i + b'}</M> contributes{' '}
          <M>{'x_i'}</M> (for w) or 1 (for b). Read it like this: <em>if we’re predicting too high, and x is big, w is the
          culprit — turn it down.</em>
        </Callout>
      </Step>

      <Step gate="q-landscape">
        <H>Watch it learn</H>
        <Figure n="6.1" wide caption="Left: the data and the current line. Right: the loss landscape over every possible (w, b) — the stronger the violet, the lower the loss. Press Train and watch the point roll downhill while the line snaps into place. Click the landscape to start somewhere else.">
          <TrainingLoopViz />
        </Figure>
        <Quiz
          id="q-landscape"
          question="Why does the path on the landscape curve instead of heading straight to the best point?"
          options={[
            { label: 'It always moves in the steepest-downhill direction from where it currently is, and that direction changes as it moves.', correct: true },
            { label: 'The learning rate is too small.', why: 'A small learning rate would just make the same curved path slower.' },
            { label: 'There’s randomness in each step.', why: 'This is full-batch gradient descent — every step is deterministic.' },
          ]}
          explain="The valley here is long and narrow (w and b are tangled together because x is never near 0), so steepest-descent first drops into the valley floor and then slides along it."
        />
      </Step>

      <Step gate="train-line">
        <H>The training loop</H>
        <P>Every training loop you’ll ever write has this skeleton:</P>
        <Pre>{`for (each epoch) {
  1. predict:   ŷ = model(x)          (the "forward pass")
  2. measure:   loss = L(ŷ, y)
  3. gradient:  how does the loss change with each parameter?
  4. update:    parameter -= learningRate × gradient
}`}</Pre>
        <P>
          An <strong>epoch</strong> is one full pass over the training data. Write it for linear regression:
        </P>
        <Exercise
          id="train-line"
          title="Train a line with gradient descent"
          {...EX.trainLine}
          hints={[
            <>
              Inside the epoch loop, loop over the examples: <C>const err = w * xs[i] + b - ys[i]</C>, then add{' '}
              <C>(2 / n) * err * xs[i]</C> to gradW and <C>(2 / n) * err</C> to gradB.
            </>,
            <>
              After the inner loop: <C>losses.push(loss)</C>, then <C>w -= lr * gradW</C> and <C>b -= lr * gradB</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-sgd">
        <H>Mini-batches: learning from a sample</H>
        <P>
          Computing the gradient over <em>every</em> example is expensive when you have millions. Instead, estimate it from a
          small random <strong>mini-batch</strong> each step. The estimate is noisy, but each step is thousands of times cheaper
          — and the noise even helps escape bad spots. This is <strong>stochastic gradient descent</strong> (SGD), and it’s how
          virtually all neural networks are trained.
        </P>
        <Exercise id="sgd-example" title="SGD with mini-batches of 16" {...EX.sgdExample} />
        <Quiz
          id="q-sgd"
          question="Why is the SGD loss curve jagged instead of smooth?"
          options={[
            { label: 'Each step measures the loss on a different small random batch, so it fluctuates.', correct: true },
            { label: 'The model is getting worse half the time.', why: 'The trend is clearly downward — the wiggles are measurement noise.' },
            { label: 'The learning rate keeps changing.', why: 'The learning rate is constant here.' },
          ]}
        />
        <Callout kind="why">
          That loop — predict, measure, gradient, update — is the beating heart of AI. The rest of the course swaps in more
          powerful models and better ways to compute the gradient, but the loop never changes.
        </Callout>
      </Step>
    </Lesson>
  )
}
